/**
 * iter109 — Personnel Tracking main page.
 *
 * Independent business domain. Not linked to /users — this page manages the
 * `employees` domain only. Two tabs: Aktif (default) + Ayrılan.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Btn, Modal, Input, Select, Badge, Textarea, NumInput } from "@/components/UI";
import { Plus, UserPlus, UserX, Cake, CalendarClock, Award, Download, Trash2, FileText, PlusCircle, ChevronRight, RotateCcw, Pencil, ExternalLink, Eye } from "lucide-react";
import PersonnelQuickAction from "@/components/PersonnelQuickAction";
import HygieneModal from "@/components/HygieneModal";
import SuitabilityModal from "@/components/SuitabilityModal";
import AccountingReportPanel from "@/components/AccountingReportPanel";
import IncidentReportsModal from "@/components/IncidentReportsModal";
import { todayTR } from "@/lib/date";

const COMPANY_OPTIONS = [
  { id: "META_PHARMA", label: "Meta Pharma" },
  { id: "LAVION", label: "Lavion" },
  { id: "PINUS", label: "Pinus" },
];

const today = () => todayTR();

export default function PersonnelPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("active");
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickEmpId, setQuickEmpId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [alerts, setAlerts] = useState({ birthdays: [], anniversaries: [], upcoming_leaves: [] });
  // iter281 — Tutanak modalı state
  const [incidentOpen, setIncidentOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/employees?status=${tab}`);
      setEmployees(r.employees || []);
    } catch (e) { console.error(e); }
  }, [tab]);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => {
    api.get("/personnel/alerts").then(setAlerts).catch(() => {});
  }, [reloadKey]);

  return (
    <div>
      <div className="flex justify-between items-start">
        <PageHeader title="Personel Takip" description="Personel yönetimi, izinler, mesailer, hakediş" />
        <div className="flex gap-2 mt-2">
          {/* iter281 — Tutanak butonu (Personel Olay Tutanağı) */}
          <Btn variant="secondary" onClick={() => setIncidentOpen(true)} data-testid="pn-incident-btn"
            className="border-amber-300 text-amber-700 hover:bg-amber-50">
            <FileText size={16}/> Tutanak
          </Btn>
          <Btn onClick={() => setQuickOpen(true)} data-testid="pn-quick-btn"
            className="bg-gradient-to-r from-violet-500 to-purple-500 text-white font-bold px-4 py-2 shadow-lg">
            <PlusCircle size={16}/> İşlem Ekle
          </Btn>
          <Btn variant="secondary" onClick={() => setNewOpen(true)} data-testid="pn-new-emp-btn">
            <UserPlus size={16}/> Personel Ekle
          </Btn>
        </div>
      </div>

      {/* Alert summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <AlertCard icon={<Cake size={16}/>} title="Doğum Günü" items={alerts.birthdays || []}
          empty="Yaklaşan doğum günü yok" testid="pn-alerts-birthdays"
          renderItem={b => `${b.employee_name} · ${b.days_left === 0 ? "BUGÜN" : `${b.days_left} gün kaldı`}`}/>
        <AlertCard icon={<CalendarClock size={16}/>} title="Yaklaşan İzin" items={alerts.upcoming_leaves || []}
          empty="Yaklaşan izin yok" testid="pn-alerts-leaves"
          renderItem={l => `${l.employee_name} · ${l.kind === "ANNUAL_LEAVE" ? "Yıllık" : "Günlük"} · ${l.days_left === 0 ? "BUGÜN" : `${l.days_left} gün kaldı`}`}/>
        <AlertCard icon={<Award size={16}/>} title="İş Yıldönümü" items={alerts.anniversaries || []}
          empty="Yaklaşan yıldönümü yok" testid="pn-alerts-anniversaries"
          renderItem={a => `${a.employee_name} · ${a.year_completing}. yıl · ${a.days_left === 0 ? "BUGÜN" : `${a.days_left} gün kaldı`}`}/>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        <button onClick={() => setTab("active")} data-testid="pn-tab-active"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${tab === "active" ? "border-violet-600 text-violet-700 bg-violet-50/40" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Çalışan Personel
        </button>
        <button onClick={() => setTab("left")} data-testid="pn-tab-left"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${tab === "left" ? "border-violet-600 text-violet-700 bg-violet-50/40" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Ayrılan Personel
        </button>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="p-2 text-left">Ad Soyad</th>
              <th className="p-2 text-left">Görev</th>
              <th className="p-2 text-left">Bölüm</th>
              <th className="p-2 text-left">İşe Giriş</th>
              <th className="p-2 text-right">Maaş</th>
              <th className="p-2 text-right">Günlük Ücret</th>
              <th className="p-2 text-center">Durum</th>
              <th className="p-2 text-center">İşlem</th>
              <th className="p-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 && (
              <tr><td colSpan={9} className="p-4 text-center text-slate-400 text-xs">Kayıt yok</td></tr>
            )}
            {employees.map(e => (
              <tr key={e.id} className="border-t hover:bg-slate-50/70 cursor-pointer" onClick={() => setSelectedEmp(e.id)} data-testid={`pn-emp-row-${e.id}`}>
                <td className="p-2 font-medium">{e.first_name} {e.last_name}</td>
                <td className="p-2 text-slate-600">{e.role || "-"}</td>
                <td className="p-2 text-slate-600">{e.department || "-"}</td>
                <td className="p-2 font-mono text-xs">{e.start_date}</td>
                <td className="p-2 text-right font-mono">{Number(e.salary || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</td>
                <td className="p-2 text-right font-mono">{Number(e.daily_wage || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</td>
                <td className="p-2 text-center"><Badge level={e.status === "active" ? "ok" : "off"}>{e.status === "active" ? "Aktif" : "Ayrıldı"}</Badge></td>
                <td className="p-2 text-center">
                  <button
                    onClick={(ev) => { ev.stopPropagation(); setQuickEmpId(e.id); }}
                    data-testid={`pn-emp-quick-${e.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition"
                    title="Hareket Ekle">
                    <PlusCircle size={12}/> Hareket
                  </button>
                </td>
                <td className="p-2"><ChevronRight size={14} className="text-slate-400"/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {/* iter281 — Tutanak Modalı */}
      <IncidentReportsModal open={incidentOpen} onClose={() => setIncidentOpen(false)}/>

      {newOpen && <NewEmployeeModal open={newOpen} onClose={() => setNewOpen(false)} onSaved={() => { setNewOpen(false); setReloadKey(k => k + 1); }}/>}
      {quickOpen && <PersonnelQuickAction open={quickOpen} onClose={() => setQuickOpen(false)} onSaved={() => setReloadKey(k => k + 1)}/>}
      {quickEmpId && (
        <PersonnelQuickAction
          open={true}
          preselectedEmployeeIds={[quickEmpId]}
          onClose={() => setQuickEmpId(null)}
          onSaved={() => { setQuickEmpId(null); setReloadKey(k => k + 1); }}
        />
      )}
      {selectedEmp && <EmployeeDetailModal eid={selectedEmp} onClose={() => setSelectedEmp(null)} onChanged={() => setReloadKey(k => k + 1)} isAdmin={isAdmin}/>}

      <AccountingReportPanel />
    </div>
  );
}

function AlertCard({ icon, title, items, empty, renderItem, testid }) {
  return (
    <Card className="p-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-violet-600">{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{title}</span>
        <span className="ml-auto text-xs font-mono bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">{items.length}</span>
      </div>
      {items.length === 0 && <div className="text-xs text-slate-400 italic">{empty}</div>}
      {items.slice(0, 5).map((it, i) => (
        <div key={i} className="text-xs text-slate-700 py-0.5 truncate" title={renderItem(it)}>{renderItem(it)}</div>
      ))}
    </Card>
  );
}

function NewEmployeeModal({ open, onClose, onSaved }) {
  return (
    <EmployeeFormModal open={open} onClose={onClose} onSaved={onSaved} mode="create" />
  );
}

/** iter116 — Personel Bilgileri Düzenleme modalı.
 * Aynı EmployeeFormModal'ı `mode="edit"` ile açar; PUT /api/employees/{eid} ile
 * mevcut kaydı günceller (audit trail'e before/after otomatik yazılır — backend
 * update_employee'de). Personnel movements / documents ETKİLENMEZ. */
function EditEmployeeModal({ open, emp, onClose, onSaved }) {
  return (
    <EmployeeFormModal open={open} onClose={onClose} onSaved={onSaved} mode="edit" initial={emp} />
  );
}

/** Shared create/edit form — tek UI, iki mod. */
function EmployeeFormModal({ open, onClose, onSaved, mode = "create", initial = null }) {
  const [f, setF] = useState(() => ({
    first_name: initial?.first_name || "",
    last_name: initial?.last_name || "",
    birth_date: initial?.birth_date || "",
    start_date: initial?.start_date || today(),
    role: initial?.role || "",
    department: initial?.department || "",
    phone: initial?.phone || "",
    email: initial?.email || "",
    salary: Number(initial?.salary || 0),
    daily_wage: Number(initial?.daily_wage || 0),
    note: initial?.note || "",
  }));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.first_name.trim() || !f.last_name.trim() || !f.start_date) return alert("Ad, soyad ve işe giriş tarihi zorunlu");
    setSaving(true);
    try {
      if (mode === "edit" && initial?.id) {
        await api.put(`/employees/${initial.id}`, f);
      } else {
        await api.post("/employees", f);
      }
      onSaved();
    } catch (e) {
      alert("Hata: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  const title = mode === "edit" ? "Personel Bilgilerini Düzenle" : "Yeni Personel";
  const tPrefix = mode === "edit" ? "pn-edit" : "pn-new";
  return (
    <Modal open={open} onClose={onClose} title={title} size="lg">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Ad" value={f.first_name} onChange={e => setF({ ...f, first_name: e.target.value })} data-testid={`${tPrefix}-first`}/>
        <Input label="Soyad" value={f.last_name} onChange={e => setF({ ...f, last_name: e.target.value })} data-testid={`${tPrefix}-last`}/>
        <Input label="Doğum Tarihi" type="date" value={f.birth_date} onChange={e => setF({ ...f, birth_date: e.target.value })} data-testid={`${tPrefix}-birth`}/>
        <Input label="İşe Giriş Tarihi" type="date" value={f.start_date} onChange={e => setF({ ...f, start_date: e.target.value })} data-testid={`${tPrefix}-start`}/>
        <Input label="Görev" value={f.role} onChange={e => setF({ ...f, role: e.target.value })} data-testid={`${tPrefix}-role`}/>
        <Input label="Bölüm" value={f.department} onChange={e => setF({ ...f, department: e.target.value })} data-testid={`${tPrefix}-department`}/>
        <Input label="Telefon" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid={`${tPrefix}-phone`}/>
        <Input label="E-posta" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid={`${tPrefix}-email`}/>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">Aylık Maaş (TL)</span>
          <NumInput step="0.01" value={f.salary} onChange={e => setF({ ...f, salary: parseFloat(e.target.value) || 0 })}
            className="w-full px-2.5 py-1.5 text-sm border rounded-md" data-testid={`${tPrefix}-salary`}/>
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500 block mb-1">Günlük Ücret (TL)</span>
          <NumInput step="0.01" value={f.daily_wage} onChange={e => setF({ ...f, daily_wage: parseFloat(e.target.value) || 0 })}
            className="w-full px-2.5 py-1.5 text-sm border rounded-md" data-testid={`${tPrefix}-wage`}/>
        </label>
        <div className="md:col-span-2"><Textarea label="Not" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} rows={2}/></div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="secondary" onClick={onClose} data-testid={`${tPrefix}-cancel`}>Vazgeç</Btn>
        <Btn onClick={save} disabled={saving} data-testid={`${tPrefix}-save`}>
          {saving ? "Kaydediliyor..." : (mode === "edit" ? "Değişiklikleri Kaydet" : "Kaydet")}
        </Btn>
      </div>
    </Modal>
  );
}

function SignaturePanel({ eid, hasSig }) {
  const [url, setUrl] = useState(null);
  const [has, setHas] = useState(hasSig);
  const [busy, setBusy] = useState(false);
  const loadSig = useCallback(async () => {
    if (!has) { setUrl(null); return; }
    try { const b = await api.getBlob(`/employees/${eid}/signature`); setUrl(URL.createObjectURL(b)); }
    catch { setUrl(null); }
  }, [eid, has]);
  useEffect(() => { loadSig(); }, [loadSig]);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.postForm(`/employees/${eid}/signature`, fd); setHas(true); setTimeout(loadSig, 0); }
    catch (err) { alert(err.response?.data?.detail || "İmza yüklenemedi"); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Islak imza silinsin mi?")) return;
    try { await api.del(`/employees/${eid}/signature`); setHas(false); setUrl(null); }
    catch (err) { alert(err.response?.data?.detail || "Silinemedi"); }
  };
  return (
    <div className="mb-4 border rounded p-2" data-testid="pn-signature-panel">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-semibold text-slate-700">Islak İmza</h4>
        <div className="flex gap-1">
          <label className="text-xs cursor-pointer bg-violet-600 text-white px-2 py-1 rounded hover:bg-violet-700" data-testid="pn-signature-upload">
            <input type="file" hidden accept="image/png,image/jpeg" onChange={upload} disabled={busy}/> {busy ? "Yükleniyor..." : has ? "Değiştir" : "+ İmza Yükle"}
          </label>
          {has && <button onClick={remove} className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50" data-testid="pn-signature-delete">Sil</button>}
        </div>
      </div>
      {url ? <img src={url} alt="İmza" className="h-16 object-contain bg-white border rounded" data-testid="pn-signature-preview"/>
        : <div className="text-xs text-slate-400 italic">İmza yüklenmemiş (PNG/JPG). PDF onay kutularında bu personelin onaylarına otomatik basılır.</div>}
    </div>
  );
}

function EmployeeDetailModal({ eid, onClose, onChanged, isAdmin }) {
  const [data, setData] = useState(null);
  const [leftOpen, setLeftOpen] = useState(false);
  const [quickForEmp, setQuickForEmp] = useState(false);
  const [formMv, setFormMv] = useState(null); // movement selected for Form Oluştur
  const [deleteMv, setDeleteMv] = useState(null); // movement selected for Sil
  const [deleteDoc, setDeleteDoc] = useState(null); // iter115: Özlük belgesi Sil
  const [editOpen, setEditOpen] = useState(false); // iter116: Personel bilgileri düzenle
  const [hygOpen, setHygOpen] = useState(false); // Iter 204 PART B
  const [suitOpen, setSuitOpen] = useState(false); // Iter 208 PART A
  // iter286 — Hareket satırına tıklanınca açılan önizleme + Tutanak yönlendirme.
  const [preview, setPreview] = useState(null); // {title, pdfUrl?, html?, goto?, gotoLabel?}
  const [gotoIncidentId, setGotoIncidentId] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/employees/${eid}`);
      setData(r);
    } catch (e) { console.error(e); }
  }, [eid]);
  useEffect(() => { load(); }, [load]);

  const markLeft = async () => {
    const d = window.prompt("Ayrılış tarihi (YYYY-MM-DD):", today());
    if (!d) return;
    try {
      await api.post(`/employees/${eid}/left`, { left_date: d });
      onChanged();
      onClose();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const reactivate = async () => {
    if (!window.confirm("Personeli tekrar aktifleştir?")) return;
    try {
      await api.post(`/employees/${eid}/reactivate`);
      onChanged();
      onClose();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const remove = async () => {
    if (!isAdmin) return;
    if (!window.confirm("PERSONELİ TAMAMEN SİLMEK istediğinize emin misiniz? Geçmiş hareketleri ve belgeleri korunacaktır.")) return;
    try {
      await api.delete(`/employees/${eid}`);
      onChanged();
      onClose();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  // iter286 — Hareket satırına tıklama davranışı: türüne göre önizleme aç.
  const openMovementPreview = async (m) => {
    // Tutanak → PDF önizleme + "Tutanak Takip'e Git" butonu
    if (m.kind === "INCIDENT" && m.incident_id) {
      try {
        const blob = await api.getBlob(`/personnel/incident-reports/${m.incident_id}/pdf`);
        const objectUrl = URL.createObjectURL(blob);
        setPreview({
          title: `Tutanak ${m.incident_no || ""} · ${m.start_date}`,
          pdfUrl: objectUrl,
          filename: `${m.incident_no || "tutanak"}.pdf`,
          goto: () => {
            URL.revokeObjectURL(objectUrl);
            setPreview(null);
            setGotoIncidentId(m.incident_id);
          },
          gotoLabel: "Tutanak Takip'e Git →",
        });
      } catch (e) { alert(e?.response?.data?.detail || "Tutanak PDF alınamadı"); }
      return;
    }
    // İzin/Rapor → aktif form varsa PDF önizleme, yoksa "Form Oluştur"
    if (["DAILY_LEAVE", "ANNUAL_LEAVE", "REPORT"].includes(m.kind)) {
      const activeForm = (m.forms || []).find(f => f.status === "ACTIVE");
      if (activeForm) {
        try {
          const blob = await api.getBlob(`/employees/forms/${activeForm.id}/pdf`);
          const objectUrl = URL.createObjectURL(blob);
          setPreview({
            title: `${activeForm.form_number || "Form"} · ${m.start_date}`,
            pdfUrl: objectUrl,
            filename: `${activeForm.form_number || "form"}.pdf`,
          });
        } catch (e) { alert(fileErrorMessage(e)); }
      } else {
        setFormMv(m);
      }
      return;
    }
    // Mesai/Bonus/Avans/Diğer → detay popup (form yok)
    const kindLabel = {
      OVERTIME_NORMAL: "Normal Mesai",
      OVERTIME_HOLIDAY: "Tatil Mesaisi",
      BONUS: "Prim / İkramiye",
      ADVANCE: "Avans",
    }[m.kind] || m.kind;
    setPreview({
      title: `${kindLabel} · ${m.start_date}${m.end_date && m.end_date !== m.start_date ? ` → ${m.end_date}` : ""}`,
      details: [
        ["Gün", m.days || 0],
        ["Tutar (TL)", Number(m.amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })],
        ["Maaştan Kesinti", m.deduct_from_salary ? "Evet" : "Hayır"],
        ["Not", m.notes || "—"],
      ],
    });
  };
  const uploadDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const type = window.prompt(
      files.length > 1
        ? `${files.length} belge yüklenecek. Hepsi için ortak Belge Türü (Kimlik / Sözleşme / Sağlık Raporu / Diploma / Diğer):`
        : "Belge türü (Kimlik / Sözleşme / Sağlık Raporu / Diploma / Diğer):",
      "Diğer");
    if (type === null) return;
    let fail = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("document_type", type);
      fd.append("document_name", file.name);
      fd.append("file", file);
      try { await api.postForm(`/employees/${eid}/documents`, fd); }
      catch (er) { fail++; console.error("Upload failed", file.name, er); }
    }
    if (fail > 0) alert(`${fail}/${files.length} belge yüklenemedi — konsolu kontrol edin.`);
    load();
    e.target.value = "";
  };

  if (!data) return <Modal open={true} onClose={onClose} title="Personel Detayı"><div className="p-4 text-xs text-slate-400">Yükleniyor...</div></Modal>;
  const emp = data.employee;
  const ls = data.leave_summary;
  return (
    <Modal open={true} onClose={onClose} title={`${emp.first_name} ${emp.last_name}`} size="xl">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatCard label="Toplam Hak Edilen" value={ls.total_entitled} unit="gün" tone="violet"/>
        <StatCard label="Kullanılan Yıllık İzin" value={ls.used_annual} unit="gün" tone="orange"/>
        <StatCard label="Kalan Yıllık İzin" value={ls.remaining} unit="gün" tone="emerald"/>
      </div>
      {/* iter116: Personel Bilgileri başlığı + Düzenle butonu */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-700">Personel Bilgileri</h4>
        <div className="flex gap-2">
          {/* Iter 204 §10 — Hygiene modal entry from employee detail. */}
          <Btn size="sm" variant="secondary" onClick={() => setHygOpen(true)} data-testid="pn-hygiene-btn">
            🧴 Hijyen Kontrolleri
          </Btn>
          {/* Iter 208 PART A — GMP Suitability entry (no medical data). */}
          <Btn size="sm" variant="secondary" onClick={() => setSuitOpen(true)} data-testid="pn-suitability-btn">
            🛡️ GMP Uygunluk
          </Btn>
          <Btn size="sm" variant="secondary" onClick={() => setEditOpen(true)} data-testid="pn-edit-btn">
            <Pencil size={12}/> Düzenle
          </Btn>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
        <div><span className="text-slate-500 text-xs uppercase">Görev</span><div>{emp.role || "-"}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Bölüm</span><div>{emp.department || "-"}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">İşe Giriş</span><div className="font-mono">{emp.start_date}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Doğum</span><div className="font-mono">{emp.birth_date || "-"}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Telefon</span><div>{emp.phone || "-"}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">E-posta</span><div>{emp.email || "-"}</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Aylık Maaş</span><div className="font-mono">{Number(emp.salary || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL</div></div>
        <div><span className="text-slate-500 text-xs uppercase">Günlük Ücret</span><div className="font-mono">{Number(emp.daily_wage || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL</div></div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-slate-700">Personel Hareketleri ({data.movements.length})</h4>
          <Btn size="sm" onClick={() => setQuickForEmp(true)} data-testid="pn-detail-add-btn"
            className="bg-gradient-to-r from-violet-500 to-purple-500 text-white">
            <PlusCircle size={12}/> + İşlem Ekle
          </Btn>
        </div>
        <div className="max-h-56 overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="p-1.5 text-left">Tarih</th>
                <th className="p-1.5 text-left">Tür</th>
                <th className="p-1.5 text-right">Gün</th>
                <th className="p-1.5 text-right">Tutar</th>
                <th className="p-1.5 text-center">Kesinti</th>
                <th className="p-1.5 text-center w-40">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {data.movements.length === 0 && <tr><td colSpan={6} className="p-2 text-center text-slate-400 italic">Hareket yok</td></tr>}
              {data.movements.map(m => (
                <tr key={m.id} className="border-t hover:bg-violet-50/50 cursor-pointer transition"
                    onClick={() => openMovementPreview(m)}
                    data-testid={`pn-mov-${m.id}`} title="Detay için tıklayın">
                  <td className="p-1.5 font-mono">{m.start_date}{m.end_date && m.end_date !== m.start_date ? ` → ${m.end_date}` : ""}</td>
                  <td className="p-1.5">
                    {m.kind === "INCIDENT" ? (
                      <span className="inline-flex items-center gap-1">
                        <FileText size={11} className="text-amber-600"/>
                        <span className="text-amber-700 font-semibold">TUTANAK</span>
                        {m.incident_no && <span className="text-[10px] text-slate-500 font-mono">{m.incident_no}</span>}
                        {m.incident_status && (
                          <span className={`text-[9px] px-1 py-0.5 rounded border ${
                            m.incident_status === "draft" ? "bg-slate-100 text-slate-700 border-slate-300" :
                            m.incident_status === "closed" ? "bg-gray-200 text-gray-700 border-gray-300" :
                            "bg-amber-100 text-amber-800 border-amber-300"
                          }`}>{
                            {draft: "Taslak", prepared: "Hazırlandı", signed_uploaded: "İmzalı",
                             defense_requested: "Savunma İstendi", defense_received: "Savunma Alındı",
                             evaluation_completed: "Değerlendirildi", closed: "Kapatıldı"}[m.incident_status] || m.incident_status
                          }</span>
                        )}
                      </span>
                    ) : m.kind}
                  </td>
                  <td className="p-1.5 text-right font-mono">{m.days}</td>
                  <td className="p-1.5 text-right font-mono">{Number(m.amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</td>
                  <td className="p-1.5 text-center">{m.deduct_from_salary ? "Evet" : "-"}</td>
                  <td className="p-1.5 text-center">
                    <div className="flex justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {["DAILY_LEAVE","ANNUAL_LEAVE","REPORT"].includes(m.kind) && (
                        <button onClick={() => setFormMv(m)} data-testid={`pn-form-${m.id}`}
                          className="text-[10px] px-2 py-0.5 bg-indigo-500 text-white rounded hover:bg-indigo-600" title="Form Oluştur">
                          Form
                        </button>
                      )}
                      {(m.forms || []).length > 0 && (
                        <span className="text-[10px] px-1 py-0.5 bg-slate-100 text-slate-600 rounded">
                          {(m.forms || []).filter(f => f.status === "ACTIVE").length}/{(m.forms || []).length} form
                        </span>
                      )}
                      {isAdmin && m.kind !== "INCIDENT" && (
                        <button onClick={() => setDeleteMv(m)} data-testid={`pn-del-${m.id}`}
                          className="text-[10px] px-2 py-0.5 bg-rose-500 text-white rounded hover:bg-rose-600" title="Sil">
                          Sil
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SignaturePanel eid={eid} hasSig={!!emp.signature_ref}/>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-slate-700">Özlük Dosyası ({data.documents.length})</h4>
          <label className="text-xs cursor-pointer bg-violet-600 text-white px-2 py-1 rounded hover:bg-violet-700" data-testid="pn-upload-doc" title="Birden fazla belge seçebilirsiniz">
            <input type="file" hidden multiple onChange={uploadDoc}/> + Belge Yükle (Toplu)
          </label>
        </div>
        <div className="max-h-32 overflow-y-auto border rounded divide-y">
          {data.documents.length === 0 && <div className="p-2 text-center text-xs text-slate-400 italic">Belge yok</div>}
          {data.documents.map(d => (
            <div key={d.id} className="flex items-center gap-2 p-1.5 text-xs" data-testid={`pn-doc-${d.id}`}>
              <FileText size={12} className="text-slate-400"/>
              <span className="flex-1">{d.document_type} · {d.document_name}</span>
              <button
                onClick={() => openEmployeeDoc(eid, d)}
                className="text-violet-600 hover:underline"
                data-testid={`pn-doc-open-${d.id}`}
              >Aç</button>
              <button
                onClick={() => downloadEmployeeDoc(eid, d)}
                className="text-violet-600 hover:underline"
                data-testid={`pn-doc-download-${d.id}`}
              >İndir</button>
              {isAdmin && (
                <button
                  onClick={() => setDeleteDoc(d)}
                  className="text-rose-600 hover:text-rose-800"
                  title="Belgeyi sil (Sistem Yetkilisi)"
                  data-testid={`pn-doc-delete-${d.id}`}
                ><Trash2 size={11}/></button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between gap-2">
        <div className="flex gap-2">
          {emp.status === "active" ? (
            <Btn variant="secondary" onClick={markLeft} data-testid="pn-mark-left">
              <UserX size={14}/> Personel Ayrıldı
            </Btn>
          ) : (
            <Btn variant="secondary" onClick={reactivate} data-testid="pn-reactivate">
              <RotateCcw size={14}/> Aktifleştir
            </Btn>
          )}
          {isAdmin && (
            <Btn onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="pn-hard-delete">
              <Trash2 size={14}/> Kalıcı Sil (Admin)
            </Btn>
          )}
        </div>
        <Btn onClick={onClose}>Kapat</Btn>
      </div>
      {quickForEmp && (
        <PersonnelQuickAction open={quickForEmp} onClose={() => setQuickForEmp(false)}
          onSaved={() => { load(); onChanged(); }}
          preselectedEmployeeIds={[eid]} />
      )}
      {formMv && (
        <FormCreateModal movement={formMv} employee={emp}
          onClose={() => setFormMv(null)}
          onCreated={() => { load(); setFormMv(null); }} />
      )}
      {deleteMv && (
        <DeleteMovementModal movement={deleteMv} employee={emp}
          onClose={() => setDeleteMv(null)}
          onDeleted={() => { load(); onChanged(); setDeleteMv(null); }} />
      )}
      {deleteDoc && (
        <DeleteDocumentModal document={deleteDoc} employee={emp} eid={eid}
          onClose={() => setDeleteDoc(null)}
          onDeleted={() => { load(); setDeleteDoc(null); }} />
      )}
      {editOpen && (
        <EditEmployeeModal
          open={editOpen}
          emp={emp}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); onChanged(); }}
        />
      )}
      {/* Iter 204 PART B — Hygiene modal wiring. */}
      <HygieneModal employee={emp} open={hygOpen} onClose={() => setHygOpen(false)} />
      {/* Iter 208 PART A — Personnel GMP Suitability modal wiring. */}
      <SuitabilityModal employee={emp} open={suitOpen} onClose={() => setSuitOpen(false)} />
      {/* iter286 — Hareket satırı tıklama önizleme modal'ı */}
      {preview && <MovementPreviewModal preview={preview} onClose={() => {
        if (preview.pdfUrl) URL.revokeObjectURL(preview.pdfUrl);
        setPreview(null);
      }}/>}
      {/* iter286 — Movement satırından "Git" ile açılan Tutanak Takip modal'ı */}
      {gotoIncidentId && (
        <IncidentReportsModal open={true} onClose={() => setGotoIncidentId(null)}
                              focusEmployeeId={eid} focusIncidentId={gotoIncidentId} />
      )}
    </Modal>
  );
}

/** iter286 — Hareket satırı önizleme modal'ı. PDF ise iframe, değilse detay listesi.
 *  "goto" callback verilirse ilgili modüle yönlendirme butonu gösterir. */
function MovementPreviewModal({ preview, onClose }) {
  return (
    <Modal open={true} onClose={onClose} title={preview.title} size="lg">
      {preview.pdfUrl ? (
        <div className="flex flex-col gap-2" data-testid="pn-mov-preview-pdf">
          <iframe src={preview.pdfUrl} title={preview.title}
                  className="w-full border rounded" style={{ height: "70vh" }}/>
          <div className="flex justify-end gap-2">
            <a href={preview.pdfUrl} download={preview.filename || "document.pdf"}
               className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border rounded hover:bg-slate-50"
               data-testid="pn-mov-preview-download">
              <Download size={12}/> İndir
            </a>
            {preview.goto && (
              <button onClick={preview.goto} data-testid="pn-mov-preview-goto"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600">
                <ExternalLink size={12}/> {preview.gotoLabel || "Detaya Git →"}
              </button>
            )}
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200">Kapat</button>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="pn-mov-preview-details">
          <table className="w-full text-sm">
            <tbody>
              {(preview.details || []).map(([k, v], i) => (
                <tr key={i} className="border-t">
                  <td className="py-1.5 pr-2 text-slate-500 font-medium w-40">{k}</td>
                  <td className="py-1.5 whitespace-pre-wrap">{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end gap-2 pt-2">
            {preview.goto && (
              <button onClick={preview.goto} data-testid="pn-mov-preview-goto"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600">
                <ExternalLink size={12}/> {preview.gotoLabel || "Detaya Git →"}
              </button>
            )}
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded bg-slate-100 hover:bg-slate-200">Kapat</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** iter114: Authenticated open/download for Personnel documents.
 * Both manuel uploaded (PDF/photo/identity/etc.) and system-generated
 * (İzin/Rapor formu PDF) belgeler AYNI shared authenticated file flow
 * kullanır → "Kimlik doğrulama gerekli" hatası ortadan kalkar (spec §2).
 * Personnel documents STAY PRIVATE (spec §7): auth Bearer token +
 * reports.view permission — no public URL.
 *
 * Error semantics (spec §23):
 *   401 → "Oturumunuz sona ermiş olabilir. Lütfen tekrar giriş yapın."
 *   403 → "Bu belgeyi görüntüleme yetkiniz yok."
 *   404 → "Belge dosyası bulunamadı."
 */
function fileErrorMessage(err) {
  const s = err?.response?.status;
  if (s === 401) return "Oturumunuz sona ermiş olabilir. Lütfen tekrar giriş yapın.";
  if (s === 403) return "Bu belgeyi görüntüleme yetkiniz yok.";
  if (s === 404) return "Belge dosyası bulunamadı.";
  return "Belge açılamadı.";
}

async function openEmployeeDoc(eid, doc) {
  try {
    await api.openBlob(`/employees/${eid}/documents/${doc.id}/download`);
  } catch (e) {
    alert(fileErrorMessage(e));
  }
}

async function downloadEmployeeDoc(eid, doc) {
  try {
    await api.downloadFile(
      `/employees/${eid}/documents/${doc.id}/download`,
      doc.document_name || doc.original_name || "document",
    );
  } catch (e) {
    alert(fileErrorMessage(e));
  }
}

async function openEmployeeForm(formId, formNumber) {
  try {
    await api.openBlob(`/employees/forms/${formId}/pdf`);
  } catch (e) {
    alert(fileErrorMessage(e));
  }
}

async function downloadEmployeeForm(formId, formNumber) {
  try {
    await api.downloadFile(
      `/employees/forms/${formId}/pdf`,
      `${formNumber || "form"}.pdf`,
    );
  } catch (e) {
    alert(fileErrorMessage(e));
  }
}

function StatCard({ label, value, unit, tone }) {
  const tones = {
    violet: "from-violet-500 to-purple-500",
    orange: "from-orange-500 to-amber-500",
    emerald: "from-emerald-500 to-teal-500",
  };
  return (
    <div className={`p-3 rounded-lg bg-gradient-to-br ${tones[tone] || tones.violet} text-white shadow`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold font-mono">{Number(value || 0).toFixed(1)} <span className="text-xs font-normal opacity-70">{unit}</span></div>
    </div>
  );
}

/** iter111: Form Oluştur modal — firma seçimi + PDF üretimi (idempotent). */
function FormCreateModal({ movement, employee, onClose, onCreated }) {
  const [companyId, setCompanyId] = useState("META_PHARMA");
  const [loading, setLoading] = useState(false);
  const generate = async () => {
    setLoading(true);
    try {
      const form = await api.post(`/employees/movements/${movement.id}/form`, { company_id: companyId });
      // iter114: Open generated PDF via authenticated fetch — direct
      // window.open bypasses the Bearer token and returns 401.
      await openEmployeeForm(form.id, form.form_number);
      onCreated();
    } catch (e) {
      alert("Form oluşturulamadı: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  const kindLabel = { DAILY_LEAVE: "Günlük İzin Formu", ANNUAL_LEAVE: "Yıllık İzin Formu", REPORT: "Rapor Formu" }[movement.kind];
  return (
    <Modal open={true} onClose={onClose} title={kindLabel} size="md">
      <div className="text-xs text-slate-500 mb-3">Hareket: {movement.start_date}{movement.end_date && movement.end_date !== movement.start_date ? ` → ${movement.end_date}` : ""} · {movement.days} gün</div>
      <div className="text-sm font-medium text-slate-700 mb-2">Formu hangi firma için oluşturmak istiyorsunuz?</div>
      <div className="grid grid-cols-1 gap-2 mb-4">
        {COMPANY_OPTIONS.map(c => (
          <label key={c.id} className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${companyId === c.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200"}`}
            data-testid={`form-company-${c.id}`}>
            <input type="radio" checked={companyId === c.id} onChange={() => setCompanyId(c.id)} name="form-co"/>
            <span className="text-sm font-medium">{c.label}</span>
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
        <Btn onClick={generate} disabled={loading} data-testid="form-create-btn">
          <FileText size={14}/> PDF Oluştur
        </Btn>
      </div>
    </Modal>
  );
}

/** iter111: Movement Sil modal — admin-only, business-effect reversal preview. */
function DeleteMovementModal({ movement, employee, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const doDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/employees/movements/${movement.id}`);
      onDeleted();
    } catch (e) {
      alert("Silinemedi: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title="Personel Hareketini Sil" size="md">
      <div className="p-3 bg-rose-50 border border-rose-200 rounded mb-3">
        <div className="text-sm font-semibold text-rose-800 mb-1">Bu personel hareketini silmek istediğinizden emin misiniz?</div>
        <div className="text-xs text-slate-700 space-y-0.5">
          <div><b>Personel:</b> {employee.first_name} {employee.last_name}</div>
          <div><b>İşlem Türü:</b> {movement.kind}</div>
          <div><b>Tarih:</b> {movement.start_date}{movement.end_date && movement.end_date !== movement.start_date ? ` → ${movement.end_date}` : ""}</div>
          <div><b>Gün:</b> {movement.days}</div>
          {Number(movement.amount || 0) !== 0 && <div><b>Ücret Etkisi:</b> {Number(movement.amount).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} TL {movement.amount < 0 ? "(kesinti)" : ""}</div>}
          {(movement.forms || []).length > 0 && <div className="text-amber-700"><b>Form:</b> {(movement.forms || []).length} adet form otomatik <b>VOID</b> olarak işaretlenecek (silinmeyecek).</div>}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Btn variant="secondary" onClick={onClose} data-testid="del-cancel-btn">Vazgeç</Btn>
        <Btn onClick={doDelete} disabled={loading} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="del-confirm-btn">
          <Trash2 size={14}/> Sil
        </Btn>
      </div>
    </Modal>
  );
}

/** iter115 — Özlük Dosyası belgesi Sil onay modalı.
 * Admin-only. Backend soft-delete yapar; blob korunur. Kullanıcı reason girer. */
function DeleteDocumentModal({ document, employee, eid, onClose, onDeleted }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const doDelete = async () => {
    if (!reason.trim()) { alert("Silme gerekçesini girin."); return; }
    setLoading(true);
    try {
      await api.del(`/employees/${eid}/documents/${document.id}`, { reason: reason.trim() });
      onDeleted();
    } catch (e) {
      alert("Silinemedi: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title="Özlük Belgesini Sil (Sistem Yetkilisi)" size="md">
      <div className="p-3 bg-rose-50 border border-rose-200 rounded mb-3">
        <div className="text-sm font-semibold text-rose-800 mb-1">Bu Özlük Dosyası belgesini silmek istediğinize emin misiniz?</div>
        <div className="text-xs text-slate-700 space-y-0.5">
          <div><b>Personel:</b> {employee.first_name} {employee.last_name}</div>
          <div><b>Belge Türü:</b> {document.document_type}</div>
          <div><b>Dosya Adı:</b> {document.document_name}</div>
          <div className="text-amber-700 mt-1">Belge <b>SOFT-DELETE</b> edilecektir: HR listelerinde artık görünmez, ancak dosya blob'u korunur ve audit trail'e işlenir.</div>
        </div>
      </div>
      <Textarea
        label="Silme Gerekçesi (zorunlu — audit trail'e kaydedilir)"
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Örn: Yanlış belge yüklendi, güncellendi..."
        rows={3}
        data-testid="doc-del-reason"
      />
      <div className="flex justify-end gap-2 mt-3">
        <Btn variant="secondary" onClick={onClose} data-testid="doc-del-cancel">Vazgeç</Btn>
        <Btn onClick={doDelete} disabled={loading || !reason.trim()} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="doc-del-confirm">
          <Trash2 size={14}/> {loading ? "Siliniyor..." : "Sil"}
        </Btn>
      </div>
    </Modal>
  );
}
